import { PouchDoc } from './pouch';
import {
  Gender,
  ActivityLevel,
  SleepQuality,
  StressLevel,
  TrainingGoal,
  AdvancedMethodsMyoReps,
  AdvancedMethodsRMTesting,
  DedicationLevel,
} from '../enums';
import {
  BFData,
  DeclaredStrength,
  LongerFinger,
  NutritionCoachingType,
  NutritionTrackingType,
  MacroTargetsType,
  ExerciseStat,
} from './user-state';
import { TrainingSplitMode } from './training-split';
import { MuscleGrowthMain, MuscleGrowthPreferenceTable } from './muscle-growth-preference';
import { FitnessPlan, stripGeneratedPlanBackupForJournal } from './fitness-plan';

export interface IntakeFormState extends PouchDoc {
  time_utc?: string; // ISO8601
  mode?: 'new' | 'change' | null;
  intakeFormProgress?: number; // used to track steps though Intake Form wizard
  gender?: Gender;
  born?: string;
  height_cm?: number;
  height_unit?: string;
  longerFinger?: LongerFinger;
  wrist_cm?: number;
  wrist_unit?: string;
  ankle_cm?: number;
  ankle_unit?: string;
  weight_kg?: number;
  weight_unit?: string;
  bfData?: BFData;
  declaredStrength?: DeclaredStrength;
  activityLevel?: ActivityLevel;
  sleepQuality?: SleepQuality;
  stressLevel?: StressLevel;
  trainingGoalGrowth?: boolean | TrainingGoal;
  muscleGrowthPreferenceMain?: MuscleGrowthMain;
  muscleGrowthPreferenceTable?: MuscleGrowthPreferenceTable;
  advancedMethodsMyoReps?: AdvancedMethodsMyoReps;
  advancedMethodsRMTesting?: AdvancedMethodsRMTesting;
  advancedMethodsCalorieCycling?: boolean;
  /// This is not the relevant number! It is only used to recommend some days to the user. The trainingDays field is relevant.
  trainingFrequency?: number;
  lastTrainingFrequency?: number; // used to reset training days if the frequency is changed
  trainingDays?: boolean[]; // ISO weekday order (Mon=0, Sun=6)
  morningAppetite?: boolean;
  mealFrequency?: number;
  sleepHours?: number;
  wakeupTime?: string;
  bedTime?: string;
  exerciseStats?: {
    [exercise_id: string]: ExerciseStat;
  };
  nutritionCoaching?: NutritionCoachingType;
  nutritionTracking?: NutritionTrackingType;
  macroTargets?: MacroTargetsType;
  intakeDeltaPercent?: number;
  trainingVolumeWeeklyAvg?: number;
  // Which shape of training program the user picked (#4591). Missing means
  // Optimal, which is what the app has always built.
  trainingSplitMode?: TrainingSplitMode;
  maxEffortPlan?: FitnessPlan;
  maxEffortPlanIntakeFormState?: IntakeFormState;
  generatedFitnessPlan?: FitnessPlan;
}

/**
 * Remove the generated-program backup from every plan embedded in an intake-form-state
 * clone that is about to be written to the journal as a historical snapshot.
 *
 * An intake-form state can carry up to three plans: `generatedFitnessPlan`,
 * `maxEffortPlan`, and the same pair again inside the nested
 * `maxEffortPlanIntakeFormState`, which is why this walks itself. Each of them carries
 * its own `generatedPlanBackup`, and each is journalled again with every intake-form
 * snapshot.
 *
 * 🛑 Same rule as {@link stripGeneratedPlanBackupForJournal}, and it matters more here:
 * call this ONLY on a clone bound for the journal, NEVER on the live `intakeformstate`
 * singleton. On the "keep" and "volumes-only" paths the intake form stores the live plan
 * object itself into `generatedFitnessPlan` rather than a copy, so stripping the live
 * state would delete the backup out of the user's current plan directly.
 */
export function stripGeneratedPlanBackupsForJournal(state: IntakeFormState | undefined): void {
  if (!state) {
    return;
  }
  stripGeneratedPlanBackupForJournal(state.generatedFitnessPlan);
  stripGeneratedPlanBackupForJournal(state.maxEffortPlan);
  stripGeneratedPlanBackupsForJournal(state.maxEffortPlanIntakeFormState);
}

/**
 * Get training volume for a given dedication level
 * @param intakeFormState The intake form state containing maxEffortPlan
 * @param dedicationLevel The dedication level to calculate volume for
 * @returns The training volume weekly average for the given dedication level
 */
export function getTrainingVolumeForDedicationLevel(
  intakeFormState: IntakeFormState,
  dedicationLevel: DedicationLevel
): number {
  if (!intakeFormState.maxEffortPlan?.trainingVolumeWeeklyAvg) {
    return 0;
  }

  if (dedicationLevel === DedicationLevel.SlowAndSteady) {
    return intakeFormState.maxEffortPlan.trainingVolumeWeeklyAvg * 0.5;
  } else if (dedicationLevel === DedicationLevel.MaximumEffort) {
    return intakeFormState.maxEffortPlan.trainingVolumeWeeklyAvg;
  } else {
    return intakeFormState.maxEffortPlan.trainingVolumeWeeklyAvg * 0.75;
  }
}

/**
 * Get the selected training dedication level based on current training volume
 * @param intakeFormState The intake form state
 * @returns The currently selected dedication level
 */
export function getSelectedTrainingDedicationLevel(intakeFormState: IntakeFormState): DedicationLevel {
  if (!intakeFormState.trainingVolumeWeeklyAvg) {
    return DedicationLevel.Balanced;
  }

  const slowAndSteadyVolume = Math.round(getTrainingVolumeForDedicationLevel(intakeFormState, DedicationLevel.SlowAndSteady));
  const maxEffortVolume = Math.round(getTrainingVolumeForDedicationLevel(intakeFormState, DedicationLevel.MaximumEffort));

  if (intakeFormState.trainingVolumeWeeklyAvg <= slowAndSteadyVolume) {
    return DedicationLevel.SlowAndSteady;
  } else if (intakeFormState.trainingVolumeWeeklyAvg > slowAndSteadyVolume && intakeFormState.trainingVolumeWeeklyAvg < maxEffortVolume) {
    return DedicationLevel.Balanced;
  } else {
    return DedicationLevel.MaximumEffort;
  }
}

/**
 * Get the suggested training dedication level based on training frequency
 * @param trainingFrequency Number of training days per week
 * @returns The suggested dedication level
 */
export function getSuggestedTrainingDedicationLevelByFrequency(trainingFrequency: number): DedicationLevel {
  if (!trainingFrequency) {
    return DedicationLevel.Balanced;
  }

  if (trainingFrequency <= 3) {
    return DedicationLevel.SlowAndSteady;
  } else if (trainingFrequency <= 5) {
    return DedicationLevel.Balanced;
  } else {
    return DedicationLevel.MaximumEffort;
  }
}

/**
 * Get the suggested training dedication level based on intake form state
 * @param intakeFormState The intake form state
 * @returns The suggested dedication level
 */
export function getSuggestedTrainingDedicationLevel(intakeFormState: IntakeFormState): DedicationLevel {
  return getSuggestedTrainingDedicationLevelByFrequency(intakeFormState.trainingFrequency);
}

/**
 * Get the suggested nutrition dedication level
 * Currently always returns Balanced as there's no dynamic calculation
 * @returns The suggested dedication level for nutrition
 */
export function getSuggestedNutritionDedicationLevel(): DedicationLevel {
  return DedicationLevel.Balanced;
}
